import { PLACEHOLDER_TEXT } from './../pages/PerformanceDashboard/constant/PDConstant';
import { createSearchParams, NavigateFunction } from 'react-router-dom';
import { TAB_KEYS, ExcludedRedirectionEndPointsList } from 'src/pages/CaseDetails/constants';
import {
  CURRENT_ALLOCATION_CYCLE,
  CURRENT_ALLOCATION_CYCLE_FEB,
  FillColor
} from 'src/pages/Cases/constants/CasesConstants';
import { interpolatePathParams } from './interpolate';
import { logError } from './ApiHelper';
import { REDIRECT_TIMESTAMP_RECORD } from '../constants/Common.constants';
import APP_ROUTES, { APP_ROUTES_PATHS_WITH_PATH_PARAM } from '../layout/Routes';
import { createQueryParams } from './QueryParamsHelper';
import { AGENT_LIVE_LOCATION } from '../pages/LiveLocationTracker/constants/LiveLocatonTrackerConstants';
import { LOCATION_TYPE_FILTERS } from '../pages/LiveLocationTracker/constants/LiveLocationTrackerInterfaces';
import { getMaxInputDate } from '../pages/LiveLocationTracker/utils';
import { DateFormat } from './DateHelper';
import { RiskRating } from '../pages/CaseDetails/components/CustomerSummary/interfaces';
import { GenericObject } from '../types/CommonConstans';
import { SortOrder } from '../types/CommonConstans';
import { customNegExperience } from '@cybertron/negative-experience';
import { GLOBAL } from '../constants/Global';

export const casesPath = `/cases/details/lan/{accountNumber}/customer/{customerReferenceId}/caseReferenceId/{caseReferenceId}/caseBusinessVertical/{caseBusinessVertical}/tab/overview`;

export const isObjectEmpty = (object: any) => {
  for (const prop in object) {
    // eslint-disable-next-line no-prototype-builtins
    if (object.hasOwnProperty(prop)) return false;
  }
  return true;
};

export enum DEVICES {
  ANDRIOD = 'ANDRIOD',
  IOS = 'IOS'
}

export const getNumberFormat = (value: number | bigint | undefined, fractionalDigits: number) => {
  if (typeof value === 'undefined') return '';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    maximumFractionDigits: fractionalDigits,
    minimumFractionDigits: fractionalDigits,
    currency: 'INR'
  }).format(value);
};

export const formatINR = (
  value: number | bigint,
  minFractionalDigits: number,
  maxFractionalDigits: number
) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    maximumFractionDigits: maxFractionalDigits,
    minimumFractionDigits: minFractionalDigits,
    currency: 'INR'
  }).format(value);
};

export const formatAmount = (value = 0, roundOff = true, fractionalDigits = 0) => {
  const roundOffValue = roundOff ? Math.round(value * 100) / 100 : value;
  return getNumberFormat(roundOffValue, fractionalDigits);
};

export const formatAmountRoundUp = (value = 0, roundUp = true, fractionalDigits = 0) => {
  const roundUpValue = roundUp ? Math.ceil(value) : value;
  return getNumberFormat(roundUpValue, fractionalDigits);
};

/**
 * Format a number with optional rounding.
 * @param value - The number to be validated.
 * @returns true or false based on the input.
 */
export const isWholeNumber = (value: number) => Number.isInteger(value);

export const shortNumberNotation = (value: any = 0, decimals = 2, separator = '') => {
  if (!value) return '';

  const parsedValue = Number(value);
  const abbreviations = [
    { threshold: 1e7, symbol: 'Cr' },
    { threshold: 1e5, symbol: 'L' },
    { threshold: 1e3, symbol: 'K' }
  ];

  for (const { threshold, symbol } of abbreviations) {
    if (parsedValue >= threshold) {
      const result = (parsedValue / threshold).toFixed(decimals);
      return isWholeNumber(+result)
        ? Math.trunc(+result) + `${separator}${symbol}`
        : result + `${separator}${symbol}`;
    }
  }

  const result = isWholeNumber(parsedValue)
    ? Math.trunc(parsedValue).toString()
    : parsedValue.toFixed(decimals);

  return result.replace(/\.0$/, ''); // Remove trailing .0 for whole numbers
};

export const shortNumberNotationWithoutTrailingZeroes = (
  value: any = 0,
  decimals = 2,
  separator = ''
) => {
  if (typeof value !== 'number') return '';
  const parsedValue = Number(value);
  if (parsedValue >= 1e7) {
    return (parsedValue / 1e7).toFixed(decimals) + `${separator}Cr`;
  } else if (parsedValue >= 1e5) {
    return (parsedValue / 1e5).toFixed(decimals) + `${separator}L`;
  } else if (parsedValue >= 1e3) {
    return (parsedValue / 1e3).toFixed(decimals) + `${separator}K`;
  }
  return parsedValue;
};

export const toFixedWithoughtRoundOff = (value = 0, decimals = 2): string => {
  const decimalNumbers = value.toString().split('.');

  if (decimalNumbers.length === 1) {
    return value.toString();
  } else {
    const decimalPart = decimalNumbers[1].slice(0, decimals);
    return `${decimalNumbers[0]}.${decimalPart}`;
  }
};

export const shortNumberNotationWithoutTrailingZeroesAndRounding = (
  value = 0,
  decimals = 2,
  separator = '',
  minThreshold = 1e5,
  useNumberFormat = false
) => {
  if (typeof value !== 'number') return '';
  const parsedValue = Number(value);
  if (parsedValue >= 1e7 && minThreshold <= 1e7) {
    return '₹' + toFixedWithoughtRoundOff(parsedValue / 1e7, decimals) + `${separator}Cr`;
  } else if (parsedValue >= 1e5 && minThreshold <= 1e5) {
    return '₹' + toFixedWithoughtRoundOff(parsedValue / 1e5, decimals) + `${separator}L`;
  } else if (parsedValue >= 1e3 && minThreshold <= 1e3) {
    return '₹' + toFixedWithoughtRoundOff(parsedValue / 1e3, decimals) + `${separator}K`;
  }
  return useNumberFormat ? getNumberFormat(parsedValue, decimals) : '₹' + parsedValue;
};

export const shortNumberNotationStartingWithLakhs = (
  value: any = 0,
  decimals = 2,
  separator = ''
) => {
  if (!value) return '';
  const parsedValue = Number(value);
  if (parsedValue >= 1e7) {
    return (parsedValue / 1e7).toFixed(decimals) + `${separator}Cr`;
  }
  if (parsedValue >= 1e5) {
    return (parsedValue / 1e5).toFixed(decimals) + `${separator}L`;
  }
  return (parsedValue / 1e3).toFixed(decimals) + `${separator}K`;
};

export const formatAmountIndianFormat = (value: any = 0, decimals = 2) => {
  if (!value) return { number: '0.00', unit: '' };
  const parsedValue = Number(value);
  if (parsedValue >= 1e7) {
    return { number: (parsedValue / 1e7).toFixed(decimals), unit: `Cr` };
  }
  if (parsedValue >= 1e5) {
    return { number: (parsedValue / 1e5).toFixed(decimals), unit: `L` };
  }
  return { number: (parsedValue / 1e3).toFixed(decimals), unit: `K` };
};

export const formatPercent = (value = 0) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value / 100);
};

export const navigateToCaseDetails = (
  customerReferenceId: string,
  loanAccountNumber: string,
  caseReferenceId: string,
  caseBusinessVertical: string,
  navigate: NavigateFunction,
  url: string,
  queryString?: string,
  tabKey: string = TAB_KEYS.OVERVIEW
) => {
  if (!customerReferenceId || !loanAccountNumber || !caseReferenceId) {
    logError('Missing required parameters for case details navigation');
    return;
  }
  const path = interpolatePathParams(url, {
    selectedTab: tabKey,
    loanId: loanAccountNumber || '',
    caseReferenceId: caseReferenceId,
    caseBusinessVertical: caseBusinessVertical,
    customerId: customerReferenceId
  });
  //please don't set replace true this breaks browser navigation functionality
  navigate({ pathname: path, search: queryString }, { replace: false });
};

export const checkIfTimestampValid = (timestamp: string) => {
  return !isNaN(Date.parse(timestamp));
};

export const formatDate = (timestamp: string, formatOptions?: Intl.DateTimeFormatOptions) => {
  if (!checkIfTimestampValid(timestamp)) {
    return '';
  }
  return new Intl.DateTimeFormat('en-IN', {
    year: '2-digit',
    day: 'numeric',
    month: 'short',
    ...formatOptions
  }).format(new Date(timestamp));
};

export const formatDateTime = (timestamp: string) => {
  if (!checkIfTimestampValid(timestamp)) {
    return '';
  }
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(timestamp));
};

export const isRedirectionPermitted = () => {
  const isValidForRedirection =
    ExcludedRedirectionEndPointsList.filter(endPoint => window.location?.href?.includes(endPoint))
      .length === 0;
  return isValidForRedirection;
};

export const calculatePercentage = (remainingDays: number) => {
  const currentMonth = new Date().getMonth();
  const totalDaysInMonth =
    currentMonth === 1 ? CURRENT_ALLOCATION_CYCLE_FEB : CURRENT_ALLOCATION_CYCLE;
  let percent = (remainingDays / totalDaysInMonth) * 100;
  if (percent > 100 || percent < 0) {
    logError(new Error('allocation date is not valid'), 'allocation date is not valid');
  }
  if (percent < 0) percent = 0;
  if (percent > 100) percent = 100;
  return percent;
};

export const calculatePercentageProperty = (percentage: number) => {
  const propertyObj = {
    className: 'green',
    color: 'var(--green-base)',
    bgClassName: 'var(--green-base)'
  };
  for (const i in FillColor) {
    if (FillColor[i].min <= percentage && FillColor[i].max >= percentage) {
      propertyObj.className = FillColor[i].stroke;
      propertyObj.bgClassName = FillColor[i].stroke + 'CircleBG';
      propertyObj.color = FillColor[i].color;
      break;
    }
  }
  return propertyObj;
};

export const pluralisation = (word: string, count: number, suffix: string) => {
  return `${word}${count > 1 ? suffix : ''}`;
};

export const throttleFunction = (fn: Function, delay: number) => {
  let prev = 0;
  return (...args: any) => {
    const now = Date.now();
    if (now - prev > delay) {
      prev = now;
      return fn(...args);
    }
  };
};
export const isJson = (str: string) => {
  try {
    return JSON.parse(str);
  } catch (e) {
    return false;
  }
};

export const getRoundOfTimeInSeconds = (time: number) => {
  return Math.ceil(time);
};

export const formatPerformanceLevel = (level: number | string) =>
  level ? `L${Math.round(Number(level))}` : PLACEHOLDER_TEXT;

export const _map = (
  array: Array<any> | Object,
  callback: (el: any, index: number, original: unknown) => any
) => {
  const arr = [];
  let index = 0;

  if (!array) return array;

  if (Array.isArray(array)) {
    for (const el of array) arr.push(callback(el, index++, array));
    return arr;
  }

  if (typeof array === 'object') {
    for (const el in array) arr.push(callback(el, index++, array));
    return arr;
  }

  throw new Error("Please check the first parameter which should either be 'Array' or 'Object' ");
};

/**
 * Format a number with optional rounding.
 * @param value - The number to be formatted.
 * @param noRoundoff - If true, the number is formatted without rounding.
 * @returns The formatted number as a string.
 */
export const formatNumber = (value: number, noRoundoff?: boolean) => {
  if (noRoundoff) {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value);
  }
  return new Intl.NumberFormat('en-IN', {
    maximumSignificantDigits: 10
  }).format(value);
};

export const zeroImmediateThenDebounce = (callback: (...params: any[]) => any, ms = 300) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  let executedTimes = 0;
  return function (this: any, ...args: any[]) {
    if (executedTimes === 0) {
      callback.apply(this, args);
    } else {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => callback.apply(this, args), ms);
    }
    ++executedTimes;
  };
};

export function recordRedirectTime() {
  const currentRecord: number[] = JSON.parse(
    localStorage.getItem(REDIRECT_TIMESTAMP_RECORD) || '[]'
  );
  if (currentRecord.length === 2) {
    currentRecord.shift();
  }
  currentRecord.push(Date.now());
  localStorage.setItem(REDIRECT_TIMESTAMP_RECORD, JSON.stringify(currentRecord));
}

export const getNameInitials = (name: string) => {
  if (!name) return '';
  const initials = name.match(/\b\w/g) || [];
  return ((initials.shift() || '') + (initials.pop() || '')).toUpperCase();
};

export const getLink = (args: Record<string, string | number>) => {
  let path = casesPath;
  for (const key in args) {
    path = path.replaceAll(`{${key}}`, `${args[key]}`);
  }
  return path;
};

export const getMobileOperatingSystem = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window?.opera;

  if (/android/i.test(userAgent)) {
    return DEVICES.ANDRIOD;
  }

  if (/iPad|iPhone|iPod/.test(userAgent) && !window?.MSStream) {
    return DEVICES.IOS;
  }

  return 'Unknown';
};

export const isFunction = (fn: unknown): fn is (...args: any[]) => void => typeof fn === 'function';

export const getSanitizedEnumValue = (
  enumMap: Record<string, string>,
  value: string | undefined
) => {
  if (!value) return '--';
  return enumMap[value] ?? value;
};

export const getFileDownload = (url: string, isNewTab = false) => {
  const downloadLink = document.createElement('a');
  downloadLink.href = url;
  downloadLink.download = 'filename';
  downloadLink.style.display = 'none';
  if (isNewTab) {
    downloadLink.target = '_blank';
  }
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
};

export const getMultipleFileDownload = (urls: string[]) => {
  // using for chrome multiple file download limitation
  getFileDownload(urls[0]);
  urls.slice(1).forEach(url => {
    setTimeout(() => {
      getFileDownload(url);
    }, 1000);
  });
};

export const toFixedValueNotation = (value?: string | number, decimal = 2) => {
  if (!value) return '0';

  const parsedValue = Number(value);

  const result = isWholeNumber(parsedValue)
    ? Math.trunc(parsedValue).toString()
    : parsedValue.toFixed(decimal);

  return result.replace(/\.0$/, ''); // Remove trailing .0 for whole numbers
};

export const isValidEmail = (email: string) => {
  // Regular expression for validating an Email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+[a-zA-Z]$/;
  if (!emailRegex.test(email)) {
    return false;
  }
  return true;
};

export const isValidPhoneNumber = (phoneNumber: string) => {
  // Regular expression for validating a phone number
  const phoneNumberRegex = /^[6-9][0-9]{9}$/;
  return phoneNumberRegex.test(phoneNumber);
};

export async function copyToClipboard(value: string): Promise<void> {
  try {
    if (window?.navigator?.clipboard) {
      await window.navigator.clipboard.writeText(value);
    } else {
      throw new Error('Clipboard API is not supported on this browser.');
    }
  } catch (error) {
    logError(error, 'Error copying to clipboard');
  }
}

type CopyToClipboardProps = {
  text: string;
  setArrowColor: React.Dispatch<React.SetStateAction<string>>;
  setTooltipContent: React.Dispatch<React.SetStateAction<string>>;
};

export const ClickTopCopy = ({ text, setArrowColor, setTooltipContent }: CopyToClipboardProps) => {
  const successCallback = () => {
    setArrowColor('none');
    setTooltipContent('Copied!');
    setTimeout(() => {
      setTooltipContent('Click To Copy');
      setArrowColor('var(--tooltip-background-color)');
    }, 2000);
  };

  const failureCallback = (error: unknown) => {
    logError(error, 'Error copying to clipboard');
  };

  copyToClipboard(text).then(successCallback).catch(failureCallback);
};

export const handleTabClose = (event: any) => {
  event.preventDefault();
  event.returnValue = 'Are you sure you want to exit?';
  return event;
};

export const downloadContent = (content: any, name = 'bulkUploadSample.csv', type = 'text/csv') => {
  const temporaryTag = document.createElement('a');
  const file = new Blob([content], { type });
  temporaryTag.href = URL.createObjectURL(file);
  temporaryTag.download = name;
  temporaryTag.click();
};
export const isMac =
  navigator?.platform.toUpperCase().indexOf('MAC') >= 0 ||
  navigator.userAgentData?.platform.toUpperCase().indexOf('MAC') >= 0;

export const openGoogleMapsWithAddress = (address: string) => {
  const encodedAddress = encodeURIComponent(address);

  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  window.open(mapsUrl, '_blank');
};

export const openGoogleMapsWithCoordinates = (latitude: number, longitude: number) => {
  const mapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
  window.open(mapsUrl, '_blank');
};

export function searchGoogle(query: string) {
  // Construct the Google search URL
  const googleSearchUrl = `https://www.google.com/search?q=${encodeURIComponent(query)}`;

  // Open the Google search URL in a new tab
  window.open(googleSearchUrl, `_${googleSearchUrl}`);
}

export const redirectToCaseDetails = (
  loanId: string,
  customerId: string,
  caseReferenceId: string,
  caseBusinessVertical: string
) => {
  if (loanId && customerId) {
    const path = interpolatePathParams(APP_ROUTES.CASE_DETAIL.path, {
      selectedTab: TAB_KEYS.OVERVIEW,
      loanId,
      customerId,
      caseReferenceId,
      caseBusinessVertical
    });
    const pathName = window?.location?.pathname;
    const location = pathName?.includes('/new') ? `/new${path}` : path;
    window.open(location, '_blank');
  }
};

export const redirectToAgentDetails = (
  agentReferenceId: string,
  agencyCode: string,
  date: string = getMaxInputDate(DateFormat.ISO_DATE_FORMAT),
  externalAgencyPerformanceDashboard = false
) => {
  const url = createQueryParams({
    [AGENT_LIVE_LOCATION]: {
      AGENCY: agencyCode,
      AGENTID: agentReferenceId,
      LOCATION_TYPE: LOCATION_TYPE_FILTERS.ALL,
      DATE: date
    }
  });
  const pathName = window?.location?.pathname;
  const isExternal =
    pathName === APP_ROUTES.SENSEI_EXTERNAL.relativePath || externalAgencyPerformanceDashboard;
  const isCollectionsPortal = pathName?.includes('/new');
  let location;
  if (isExternal) {
    location = isCollectionsPortal
      ? `/new${APP_ROUTES_PATHS_WITH_PATH_PARAM.SENSEI.external_map_view_live_location}${url}`
      : APP_ROUTES_PATHS_WITH_PATH_PARAM.SENSEI.external_map_view_live_location + url;
  } else {
    location = isCollectionsPortal
      ? `/new${APP_ROUTES_PATHS_WITH_PATH_PARAM.SENSEI.map_view_live_location}${url}`
      : APP_ROUTES_PATHS_WITH_PATH_PARAM.SENSEI.map_view_live_location + url;
  }

  window.open(location, '_blank');
};

export function maskString(str = '', startIndex: number, length: number, maskStr = 'X'): string {
  if (startIndex < 0 || length < 0 || startIndex >= str?.length) {
    throw new Error('Invalid start index or length');
  }

  const endIndex = Math.min(startIndex + length, str.length);
  const maskedSection = maskStr.repeat(endIndex - startIndex);

  return str.slice(0, startIndex) + maskedSection + str.slice(endIndex);
}

export const getRiskBarColor = (riskSegment: string): string => {
  const riskSegmentColors: Record<string, string> = {
    [RiskRating.HIGH_RISK]: 'var(--red-border)',
    [RiskRating.MEDIUM_RISK]: 'var(--yellow-medium)',
    [RiskRating.LOW_RISK]: 'var(--light-green)'
  };

  return riskSegmentColors[riskSegment] || '';
};

export const getDateAfterCertainMonths = (date: Date, months: number) => {
  const newDate = new Date(date);
  newDate.setMonth(date.getMonth() + months);
  return newDate;
};

export const numericSort = (
  a: number | null | undefined,
  b: number | null | undefined,
  order: SortOrder = SortOrder.ASC,
  fallback = 0
): number => {
  const valueA = a ?? fallback;
  const valueB = b ?? fallback;

  if (order === SortOrder.ASC) {
    return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
  } else {
    return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
  }
};

export const createPropertySort = <T>(
  keyPath: string,
  order: SortOrder = SortOrder.ASC,
  fallback = 0
) => {
  return (a: T, b: T): number => {
    const valueA = (a as GenericObject)[keyPath] ?? fallback;
    const valueB = (b as GenericObject)[keyPath] ?? fallback;

    return numericSort(valueA, valueB, order, fallback);
  };
};

export const createGetterSort = <T>(
  getter: (item: T) => number | null | undefined,
  order: SortOrder = SortOrder.ASC,
  fallback = 0
) => {
  return (a: T, b: T): number => {
    return numericSort(getter(a), getter(b), order, fallback);
  };
};

type LogCustomNegativeExperienceProps = {
  message: string;
  extras?: Record<string, unknown>;
};

export const logCustomNegativeExperience = ({
  message,
  extras
}: LogCustomNegativeExperienceProps) => {
  customNegExperience(message, {
    ...extras,
    agentId: GLOBAL.AGENT_ID,
    sessionId: GLOBAL.LONGHORN_SESSION_IDENTIFIER,
    deviceId: GLOBAL.DEVICE_ID
  });
};
